import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { Shield, Lock, Eye, Database, UserCheck, Bell, Mail, MapPin, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function PrivacyPolicy() {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();

  const sections = [
    {
      icon: Database,
      title: language === 'ar' ? 'المعلومات التي نجمعها' : 'Information We Collect',
      content: language === 'ar' 
        ? 'نقوم بجمع المعلومات التي تقدمها لنا مباشرة عند إنشاء حساب، مثل الاسم والبريد الإلكتروني ورقم الهاتف. كما نجمع معلومات عن متجرك ومنتجاتك التي تضيفها على المنصة، بالإضافة إلى بيانات المعاملات التجارية.'
        : 'We collect information you provide directly when creating an account, such as name, email, and phone number. We also collect information about your store and products you add to the platform, as well as transaction data.'
    },
    {
      icon: Eye,
      title: language === 'ar' ? 'كيف نستخدم معلوماتك' : 'How We Use Your Information',
      content: language === 'ar'
        ? 'نستخدم المعلومات المجمعة لتقديم خدماتنا وتحسينها، ومعالجة المعاملات، والتواصل معك بشأن حسابك ومتجرك، وإرسال التحديثات والإشعارات المهمة، وضمان أمان المنصة ومنع الاحتيال.'
        : 'We use collected information to provide and improve our services, process transactions, communicate with you about your account and store, send important updates and notifications, and ensure platform security and fraud prevention.'
    },
    {
      icon: Lock,
      title: language === 'ar' ? 'حماية البيانات' : 'Data Protection',
      content: language === 'ar'
        ? 'نطبق إجراءات أمنية صارمة لحماية بياناتك الشخصية من الوصول غير المصرح به أو التغيير أو الإفشاء أو الإتلاف. نستخدم تشفير SSL/TLS لجميع عمليات نقل البيانات ونخزن البيانات الحساسة بشكل مشفر.'
        : 'We implement strict security measures to protect your personal data from unauthorized access, alteration, disclosure, or destruction. We use SSL/TLS encryption for all data transfers and store sensitive data in encrypted form.'
    },
    {
      icon: UserCheck,
      title: language === 'ar' ? 'حقوقك' : 'Your Rights',
      content: language === 'ar'
        ? 'لديك الحق في الوصول إلى بياناتك الشخصية وتصحيحها أو حذفها. يمكنك أيضاً طلب نسخة من بياناتك أو الاعتراض على معالجتها في أي وقت. للممارسة هذه الحقوق، يرجى التواصل معنا عبر البريد الإلكتروني.'
        : 'You have the right to access, correct, or delete your personal data. You can also request a copy of your data or object to its processing at any time. To exercise these rights, please contact us via email.'
    },
    {
      icon: Bell,
      title: language === 'ar' ? 'الإشعارات والتسويق' : 'Notifications & Marketing',
      content: language === 'ar'
        ? 'قد نرسل لك إشعارات متعلقة بحسابك ومتجرك. يمكنك إلغاء الاشتراك في الرسائل التسويقية في أي وقت من خلال إعدادات حسابك أو النقر على رابط إلغاء الاشتراك في أي بريد إلكتروني تسويقي.'
        : 'We may send you notifications related to your account and store. You can unsubscribe from marketing messages at any time through your account settings or by clicking the unsubscribe link in any marketing email.'
    },
    {
      icon: MapPin,
      title: language === 'ar' ? 'نقل البيانات' : 'Data Transfer',
      content: language === 'ar'
        ? 'قد يتم نقل بياناتك ومعالجتها في دول أخرى غير دولة إقامتك. نضمن أن أي نقل للبيانات يتم وفقاً للقوانين المعمول بها وبمستوى حماية مناسب.'
        : 'Your data may be transferred and processed in countries other than your country of residence. We ensure that any data transfer is done in accordance with applicable laws and with an appropriate level of protection.'
    }
  ];

  return (
    <div className={`min-h-screen cgm-page`}>
      {/* Compact Hero Section with Gradient */}
      <section className="relative overflow-hidden py-8 sm:py-10">
        {/* Background Effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-r from-[#38BDF8] to-[#F97316] opacity-90" />
          <div className="absolute top-0 end-0 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute bottom-0 start-0 w-48 h-48 rounded-full bg-white/10 blur-3xl" />
        </div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
            <span className="text-sm">{language === 'ar' ? 'الرئيسية' : 'Home'}</span>
          </Link>
          
          <div className="text-center">
            <div className="w-16 h-16 mx-auto glass-card rounded-2xl flex items-center justify-center mb-4">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3" data-testid="privacy-title">
              {language === 'ar' ? 'سياسة الخصوصية' : 'Privacy Policy'}
            </h1>
            <p className="text-base text-white/90 max-w-2xl mx-auto">
              {language === 'ar' 
                ? 'نحن في CloudGM نلتزم بحماية خصوصيتك وأمان بياناتك الشخصية'
                : 'At CloudGM, we are committed to protecting your privacy and the security of your personal data'}
            </p>
            <p className="text-xs text-white/70 mt-3">
              {language === 'ar' ? 'آخر تحديث: ديسمبر 2024' : 'Last updated: December 2024'}
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        <div className={`rounded-2xl shadow-lg p-5 sm:p-8 space-y-6 border ${
          isDarkMode 
            ? 'bg-[#131B2C] border-white/10' 
            : 'bg-white border-black/5'
        }`}>
          {/* Introduction */}
          <div className={`border-b pb-6 ${isDarkMode ? 'border-white/10' : 'border-gray-100'}`}>
            <p className={`leading-relaxed text-base ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {language === 'ar'
                ? 'مرحباً بك في CloudGM Business. تصف سياسة الخصوصية هذه كيفية جمعنا واستخدامنا وحمايتنا لمعلوماتك الشخصية عند استخدامك لمنصتنا. باستخدامك للمنصة، فإنك توافق على الممارسات الموضحة في هذه السياسة.'
                : 'Welcome to CloudGM Business. This Privacy Policy describes how we collect, use, and protect your personal information when you use our platform. By using the platform, you agree to the practices outlined in this policy.'}
            </p>
          </div>

          {/* Sections */}
          {sections.map((section, index) => (
            <div 
              key={index} 
              className={`flex gap-4 sm:gap-5 p-4 rounded-xl transition-all duration-300 hover:-translate-y-0.5 ${
                isDarkMode 
                  ? 'bg-white/5 hover:bg-white/10 border border-white/5' 
                  : 'bg-gray-50 hover:bg-gray-100/80 border border-gray-100'
              }`}
            >
              <div className="flex-shrink-0">
                <div className="w-11 h-11 rounded-xl gradient-primary flex items-center justify-center">
                  <section.icon className="h-5 w-5 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <h2 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                  {section.title}
                </h2>
                <p className={`leading-relaxed text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {section.content}
                </p>
              </div>
            </div>
          ))}

          {/* Contact */}
          <div className={`rounded-xl p-5 mt-6 border ${
            isDarkMode 
              ? 'bg-gradient-to-r from-[#38BDF8]/10 to-[#F97316]/10 border-white/10' 
              : 'bg-gradient-to-r from-[#38BDF8]/5 to-[#F97316]/5 border-[#38BDF8]/20'
          }`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center">
                <Mail className="h-5 w-5 text-white" />
              </div>
              <h2 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                {language === 'ar' ? 'تواصل معنا' : 'Contact Us'}
              </h2>
            </div>
            <p className={`mb-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {language === 'ar'
                ? 'إذا كان لديك أي أسئلة حول سياسة الخصوصية هذه أو ممارساتنا المتعلقة بالبيانات، يرجى التواصل معنا:'
                : 'If you have any questions about this Privacy Policy or our data practices, please contact us:'}
            </p>
            <div className={`space-y-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <p><strong className="text-gradient">{language === 'ar' ? 'البريد الإلكتروني:' : 'Email:'}</strong> support@cloudgm.business</p>
              <p><strong className="text-gradient">{language === 'ar' ? 'الهاتف:' : 'Phone:'}</strong> <span dir="ltr">+971 50 151 1917</span></p>
              <p><strong className="text-gradient">{language === 'ar' ? 'الموقع:' : 'Website:'}</strong> www.cloudgm.business</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
